import { useState, useEffect, useMemo } from "react";
import TxnsTView from "./TxnsTView";
import TxnsTableView from "./TxnsTableView";
import { transactionAPI } from "services";
import useUniversalStore from "store/universalStore";
import {
  ShimmerLoader,
  HeaderShimmer,
  TableShimmer
} from "components";
import { formatMoney, debounce } from "utils";

const TxnsListView = ({ animation, setAnimation, hideClosed, handleToggle }) => {
  const [txns, setTxns] = useState([]);
  const { accountId, isReminderChanged, write, selectedMonth, isSharedAccount, txnViewMode } = useUniversalStore((state) => state);
  const [loading, setLoading] = useState(false);
  const [openBalance, setOpenBalance] = useState(0);

  // Filter transactions based on hideClosed toggle
  const filteredTnx = useMemo(() => {
    if (hideClosed) {
      return txns.filter((item) => !item.cancelled);
    }
    return txns;
  }, [hideClosed, txns]);

  const monthlyCash = useMemo(() => {
    let cashIn = 0;
    let cashOut = 0;

    txns.forEach(txn => {
      if (!txn.cancelled) {
        const amount = txn.amount || 0;
        if (amount > 0) cashIn += amount;
        else cashOut += Math.abs(amount);
      }
    });

    return {
      in: formatMoney(cashIn),
      out: formatMoney(cashOut),
      totalInOutChange: cashIn - cashOut
    };
  }, [txns]);

  const closeBalance = useMemo(() => {
    return openBalance + monthlyCash.totalInOutChange;
  }, [openBalance, monthlyCash]);

  const debouncedLoadAllData = useMemo(() => {
    const fetchFunc = async (mDate, accId, isSharedAcc) => {
      if (!accId || !mDate) return;
      try {
        const startDate = new Date(mDate.getFullYear(), mDate.getMonth(), 1, 0, 0, 0, 0);
        const prevMonthEnd = new Date(startDate.getTime() - 1);

        const [txnsResult, balanceResult] = await Promise.all([
          transactionAPI.getTransactionsByAccountIdWithMonth(accId, mDate),
          transactionAPI.sumTillDateByAccount(prevMonthEnd, accId)
        ]);
        let balance = balanceResult;
        const modifiedTxns = txnsResult.map((txn) => {
          const isCancelled = txn.get("cancelled");
          const amount = isSharedAcc ? (txn.get("amount") * -1) : txn.get("amount");
          if (!isCancelled) {
            balance += amount;
          }
          return {
            id: txn.id,
            amount: amount,
            detail: txn.get("detail"),
            date: txn.get("date"),
            type: txn.get("type"),
            cancelled: isCancelled,
            attachment: txn.get("attachment"),
            balance: balance,
          }
        })

        setTxns(modifiedTxns);
        setOpenBalance(balanceResult);
      } catch (error) {
        console.error("Error loading Khata data:", error);
      } finally {
        setLoading(false);
      }
    };

    return debounce(fetchFunc, 800);
  }, []);

  useEffect(() => {
    setLoading(true);
    debouncedLoadAllData(selectedMonth, accountId, isSharedAccount);
  }, [selectedMonth, accountId, isSharedAccount]);

  return (
    <div
      className={`row p-0 m-0 ${animation}`}
      onAnimationEnd={() => setAnimation("")}>
      {loading ? (
        txnViewMode === "table" ? (
          <TableShimmer isSharedAccount={isSharedAccount} />
        ) : (
          <div className="row p-0 m-0">
            <div className="col-6 list-group list-group-flush scrollbar-design overflow-auto px-md-3"
              style={{ height: 'calc(100vh - 225px)', maxHeight: 'calc(100vh - 225px)' }}>
              <div className="shimmer-container">
                <HeaderShimmer />
                {[...Array(6)].map((_, i) => <ShimmerLoader key={i} />)}
              </div>
            </div>
            <div className="col-6 list-group list-group-flush scrollbar-design overflow-auto px-md-3"
              style={{ height: 'calc(100vh - 225px)', maxHeight: 'calc(100vh - 225px)' }}>
              <div className="shimmer-container">
                <HeaderShimmer />
                {[...Array(6)].map((_, i) => <ShimmerLoader key={i} />)}
              </div>
            </div>
          </div>
        )
      ) : txnViewMode === "table" ? (
        <TxnsTableView
          txns={filteredTnx}
          openBalance={openBalance}
          handleToggle={handleToggle}
          setLoading={setLoading}
          loadTxns={() => debouncedLoadAllData(selectedMonth, accountId, isSharedAccount)}
          selectedMonth={selectedMonth}
          isSharedAccount={isSharedAccount}
        />
      ) : (
        <TxnsTView
          txns={filteredTnx}
          monthlyCash={monthlyCash}
          openBalance={openBalance}
          closeBalance={closeBalance}
          handleToggle={handleToggle}
          setLoading={setLoading}
          loadTxns={() => debouncedLoadAllData(selectedMonth, accountId, isSharedAccount)}
        />
      )}
    </div>
  );
};

export default TxnsListView;